<template>
  <form @submit.stop.prevent="submitForm">
    <transition mode="out-in">
      <!-- Success Message -->
      <div v-if="message" class="alert alert-success mb-5">
        {{ message }}
        <button type="button" class="btn btn-sm btn-link" @click="resetForm">Create Another Order</button>
      </div>

      <!-- Main Form -->
      <div v-else>
        <!-- Error Alert -->
        <div v-if="error" class="alert alert-danger alert-dismissible mb-4">
          <h6 class="alert-heading mb-2">
            <i class="bi bi-exclamation-triangle me-2"></i>
            {{ isEditMode ? 'Failed to Update Order' : 'Failed to Create Order' }}
          </h6>
          <p class="mb-0">{{ error }}</p>
          <button type="button" class="btn-close" @click="error = null"></button>
        </div>

        <!-- Loading State -->
        <div v-if="submitting" class="alert alert-info mb-4">
          <div class="d-flex align-items-center">
            <div class="spinner-border spinner-border-sm me-3"></div>
            <div>{{ isEditMode ? 'Updating order...' : 'Creating order...' }} Please wait.</div>
          </div>
        </div>

        <!-- Order Details -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0">Order Details</h6>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Platform</label>
                <select v-model="order.platform" class="form-select">
                  <option v-for="store in supportedStores" :key="store.key" :value="store.key">{{ store.label }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">Order Date</label>
                <utc-date-input v-model="order.orderdate" />
              </div>
              <div class="col-12">
                <label class="form-label">Order ID</label>
                <input type="text" v-model="order.orderid" autocomplete="off" data-lpignore=true  class="form-control" required :disabled="isEditMode">
              </div>
            </div>
          </div>
        </div>

        <!-- Items Section -->
        <div class="card mb-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h6 class="mb-0">Items ({{ order.items.length }})</h6>
            <div class="text-muted">
              Subtotal: <money-block :dollar="subtotal" class="d-inline-block" />
            </div>
          </div>
          <div class="list-group">
            <div v-for="(item, index) in order.items" :key="item._temp_id" class="list-group-item list-group-item-action">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <h6 class="mb-0">Item {{ index + 1 }}</h6>
                  <button
                    v-if="order.items.length > 1"
                    type="button"
                    class="btn-close"
                    @click="removeItem(index)"
                    aria-label="Remove item"
                  ></button>
                </div>

                <!-- Item Selector -->
                <div class="mb-3">
                  <item-selector v-model="order.items[index]" />
                </div>

                <!-- Quantity and Price -->
                <div v-if="item.itemid" class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label small">Quantity</label>
                    <input
                      type="number"
                      v-model.number="item.quantity"
                      autocomplete="off" data-lpignore=true
                      class="form-control"
                      min="1"
                      required
                    >
                  </div>
                  <div class="col-md-4">
                    <label class="form-label small">Unit Price</label>
                    <money-input v-model="item.unit_price" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label small">Line Total</label>
                    <div class="form-control-plaintext">
                      <money-block :dollar="item.quantity * item.unit_price" />
                    </div>
                  </div>
                </div>

                <!-- COGS Info -->
                <div v-if="item.itemid" class="small text-muted mt-2">
                  <strong>COGS:</strong>
                  <money-block :dollar="parseFloat(item.cogs) * -1" class="d-inline-block" /> × {{ item.quantity }}
                  = <money-block :dollar="parseFloat(item.cogs) * item.quantity * -1" class="d-inline-block" />
                </div>
            </div>

            <div class="list-group-item">
              <button type="button" @click="addItem" class="btn btn-outline-primary btn-sm">
                <i class="bi bi-plus-circle"></i> Add Another Item
              </button>
            </div>
          </div>
        </div>

        <!-- Order Totals -->
        <div class="card mb-3">
          <div class="card-header">
            <h6 class="mb-0">Order Totals</h6>
          </div>
          <div class="card-body">
            <table class="table table-sm mb-0">
              <tbody>
                <tr>
                  <td>Items Subtotal:</td>
                  <td class="text-end">
                    <money-block :dollar="subtotal" />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label class="mb-0 form-label">Shipping Collected:</label>
                  </td>
                  <td class="text-end" style="width: 150px;">
                    <money-input v-model="order.shipping" />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label class="mb-0 form-label">Taxes Collected:</label>
                  </td>
                  <td class="text-end">
                    <money-input v-model="order.taxes" />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label class="mb-0 form-label">Shipping Label (Cost Paid):</label>
                  </td>
                  <td class="text-end">
                    <money-input v-model="order.shipping_label" />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label class="mb-0 form-label">Platform Fees:</label>
                  </td>
                  <td class="text-end">
                    <money-input v-model="order.fees" />
                  </td>
                </tr>
                <tr class="border-top">
                  <td class="lead fw-bold">Total to Seller:</td>
                  <td class="text-end lead fw-bold"><money-block :dollar="orderNet" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Buyer Info (optional) -->
        <div class="card mb-3">
          <div
            class="card-header d-flex justify-content-between align-items-center"
            style="cursor:pointer"
            @click="showBuyerSection = !showBuyerSection"
          >
            <h6 class="mb-0">Buyer Info <span class="text-muted fw-normal">(optional)</span></h6>
            <div class="d-flex align-items-center gap-2">
              <span v-if="hasBuyerInfo" class="small text-muted">
                {{ [order.ship_city, order.ship_state, order.ship_country].filter(Boolean).join(', ') || order.buyer_username }}
              </span>
              <i :class="showBuyerSection ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="text-muted"></i>
            </div>
          </div>
          <div v-if="showBuyerSection" class="card-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label small">Buyer Username</label>
                <input type="text" v-model="order.buyer_username" autocomplete="off" data-lpignore=true class="form-control" placeholder="e.g. poshmark_handle" />
              </div>
              <div class="col-12">
                <label class="form-label small">Recipient Name</label>
                <input type="text" v-model="order.ship_name" autocomplete="off" data-lpignore=true class="form-control" placeholder="Full name" />
              </div>
              <div class="col-md-5">
                <label class="form-label small">City</label>
                <input type="text" v-model="order.ship_city" autocomplete="off" data-lpignore=true class="form-control" placeholder="City" />
              </div>
              <div class="col-md-3">
                <label class="form-label small">State</label>
                <input type="text" v-model="order.ship_state" autocomplete="off" data-lpignore=true class="form-control" placeholder="ST" maxlength="50" />
              </div>
              <div class="col-md-4">
                <label class="form-label small">ZIP</label>
                <input type="text" v-model="order.ship_zip" autocomplete="off" data-lpignore=true class="form-control" placeholder="00000" maxlength="20" />
              </div>
              <div class="col-12">
                <label class="form-label small">Country</label>
                <input type="text" v-model="order.ship_country" autocomplete="off" data-lpignore=true class="form-control" placeholder="US" maxlength="50" />
              </div>
            </div>
          </div>
        </div>

        <!-- Validation Errors -->
        <div v-if="validationErrors.length > 0" class="alert alert-warning">
          <strong>Please fix the following:</strong>
          <ul class="mb-0 mt-2">
            <li v-for="(error, index) in validationErrors" :key="index">{{ error }}</li>
          </ul>
        </div>

        <!-- Submit Button -->
        <div class="d-grid gap-3">
          <button type="submit" class="btn btn-primary btn-lg" :disabled="validationErrors.length > 0 || submitting">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            {{ submitting ? (isEditMode ? 'Updating Order...' : 'Creating Order...') : (isEditMode ? 'Save Changes' : 'Create Order') }}
          </button>
        </div>
      </div>

    </transition>
  </form>
</template>

<script setup>
import { ref, reactive, computed, defineEmits, defineProps } from 'vue'
import { useStore } from 'vuex'
import ItemSelector from "@/app/components/orders/ItemSelector.vue"
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue"
import UtcDateInput from "@/app/components/Utils/UtcDateInput.vue"
import MoneyInput from "@/app/components/Utils/MoneyInput.vue"

const props = defineProps({
  platform: {
    type: String,
    default: 'POSHMARK'
  },
  editOrder: {
    type: Object,
    default: null
  },
  prefillItems: {
    type: Array,
    default: null
  }
})

const store = useStore()
const emit = defineEmits(['saved'])
const message = ref()
const showBuyerSection = ref(true)
const error = ref(null)
const submitting = ref(false)
const isEditMode = computed(() => !!props.editOrder)
const supportedStores = computed(() => store.state.supportedStores.filter(p => p.manual))

function buildInitialOrder() {
  if (props.editOrder) {
    const eo = props.editOrder
    const lineItems = eo.lineItems || eo.line_items || []
    return {
      platform: eo.remote_store || props.platform || 'POSHMARK',
      orderdate: eo.order_date ? new Date(eo.order_date) : new Date(),
      orderid: eo.order_id || '',
      fees: parseFloat(eo.listing_fees || 0),
      shipping: parseFloat(eo.shipping || 0),
      taxes: parseFloat(eo.tax_collected || 0),
      shipping_label: Math.abs(parseFloat(eo.SHIPPING || 0)),
      buyer_username: eo.buyer_username || '',
      ship_name: eo.ship_name || '',
      ship_city: eo.ship_city || '',
      ship_state: eo.ship_state || '',
      ship_zip: eo.ship_zip || '',
      ship_country: eo.ship_country || '',
      items: lineItems.length > 0
        ? lineItems.map(li => ({
            _temp_id: Date.now() + Math.random(),
            itemid: li.remote_id || '',
            SKU: li.SKU || '',
            memo: li.item_name || '',
            quantity: li.quantity || li.quantity_purchased || 1,
            unit_price: parseFloat(li.unit_price || 0),
            cogs: parseFloat(li.source_price || li.inventory?.source_price || 0),
            photo_url: li.photo_url || li.inventory?.photo_url || ''
          }))
        : [{
            _temp_id: Date.now(),
            itemid: '',
            SKU: '',
            memo: '',
            quantity: 1,
            unit_price: 0,
            cogs: 0,
            photo_url: ''
          }]
    }
  }
  return {
    platform: props.platform || 'POSHMARK',
    orderdate: new Date(),
    orderid: '',
    fees: 0,
    shipping: 0,
    taxes: 0,
    shipping_label: 0,
    buyer_username: '',
    ship_name: '',
    ship_city: '',
    ship_state: '',
    ship_zip: '',
    ship_country: '',
    items: props.prefillItems
      ? props.prefillItems.map(i => ({ ...i, _temp_id: Date.now() + Math.random() }))
      : [{
          _temp_id: Date.now(),
          itemid: '',
          SKU: '',
          memo: '',
          quantity: 1,
          unit_price: 0,
          cogs: 0,
          photo_url: ''
        }]
  }
}

const order = reactive(buildInitialOrder())

// Auto-open buyer section in edit mode if order has buyer data
if (props.editOrder && (props.editOrder.buyer_username || props.editOrder.ship_city)) {
  showBuyerSection.value = true
}

// Computed values
const subtotal = computed(() => {
  return order.items.reduce((sum, item) => {
    return sum + (item.quantity * parseFloat(item.unit_price || 0))
  }, 0)
})

const orderTotal = computed(() => subtotal.value + parseFloat(order.shipping || 0))

const orderNet = computed(() => orderTotal.value - parseFloat(order.fees || 0))

const hasBuyerInfo = computed(() => {
  return !!(order.buyer_username || order.ship_city || order.ship_state || order.ship_zip)
})

const validationErrors = computed(() => {
  const errors = []

  if (!order.orderid || order.orderid.trim() === '') {
    errors.push('Order ID is required')
  }

  if (order.items.length === 0) {
    errors.push('At least one item is required')
  }

  order.items.forEach((item, index) => {
    if (!item.itemid) {
      errors.push(`Item ${index + 1}: No inventory item selected`)
    }
    if (!item.quantity || item.quantity < 1) {
      errors.push(`Item ${index + 1}: Invalid quantity`)
    }
    if (!item.unit_price || item.unit_price <= 0) {
      errors.push(`Item ${index + 1}: Invalid unit price`)
    }
  })

  if (subtotal.value <= 0) {
    errors.push('Subtotal must be greater than zero')
  }

  return errors
})

// Methods
function addItem() {
  order.items.push({
    _temp_id: Date.now(),
    itemid: '',
    SKU: '',
    memo: '',
    quantity: 1,
    unit_price: 0,
    cogs: 0,
    photo_url: ''
  })
}

function removeItem(index) {
  order.items.splice(index, 1)
}

async function submitForm() {
  if (validationErrors.value.length > 0) {
    return
  }

  error.value = null
  submitting.value = true

  // Transform to backend format
  const payload = {
    platform: order.platform,
    orderdate: order.orderdate,
    orderid: order.orderid,
    saleprice: orderTotal.value, // Total amount paid
    fees: parseFloat(order.fees || 0),
    shipping: parseFloat(order.shipping || 0),
    taxes: parseFloat(order.taxes || 0),
    shipping_label: parseFloat(order.shipping_label || 0),
    is_system: false, // Manual user entry
    buyer_username: order.buyer_username || null,
    ship_name: order.ship_name || null,
    ship_city: order.ship_city || null,
    ship_state: order.ship_state || null,
    ship_zip: order.ship_zip || null,
    ship_country: order.ship_country || null,
    items: order.items.map(item => ({
      itemid: item.itemid,
      SKU: item.SKU,
      memo: item.memo,
      quantity: item.quantity,
      unit_price: parseFloat(item.unit_price),
      cogs: parseFloat(item.cogs),
      ...(item.is_custom && {
        is_custom: true,
        source_price: parseFloat(item.source_price || 0),
        acquisition_date: item.acquisition_date || null,
        source_category_id: item.source_category_id || null,
        source_location_id: item.source_location_id || null,
      })
    }))
  }

  try {
    let res
    if (isEditMode.value) {
      res = await store.dispatch('eshop/updateOrder', { orderId: props.editOrder.order_id, order: payload })
    } else {
      res = await store.dispatch('eshop/addOrder', { order: payload })
    }
    // Emit saved event instead of showing message
    emit('saved', { success: true, data: res })
  } catch (err) {
    submitting.value = false

    // Better error messages
    if (err.response) {
      const status = err.response.status
      const data = err.response.data

      if (status === 400) {
        error.value = data.message || 'Invalid order data. Please check your inputs and try again.'
      } else if (status === 409) {
        error.value = `Order ID "${order.orderid}" already exists. Please use a different order ID.`
      } else if (status === 500) {
        error.value = 'Server error occurred. Your order was not created. Please try again or contact support if the problem persists.'
      } else {
        error.value = data.message || data.error || `Error ${status}: Failed to create order`
      }
    } else if (err.request) {
      error.value = 'Network error. Please check your internet connection and try again.'
    } else {
      error.value = err.message || 'An unexpected error occurred. Please try again.'
    }

    // Scroll to error message
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  } finally {
    submitting.value = false
  }
}

function resetForm() {
  message.value = null
  error.value = null
  submitting.value = false
  order.platform = props.platform || 'POSHMARK'
  order.orderdate = new Date()
  order.orderid = ''
  order.fees = 0
  order.shipping = 0
  order.taxes = 0
  order.shipping_label = 0
  order.buyer_username = ''
  order.ship_name = ''
  order.ship_city = ''
  order.ship_state = ''
  order.ship_zip = ''
  order.ship_country = ''
  showBuyerSection.value = false
  order.items = [{
    _temp_id: Date.now(),
    itemid: '',
    SKU: '',
    memo: '',
    quantity: 1,
    unit_price: 0,
    cogs: 0,
    photo_url: ''
  }]
}
</script>

<style scoped>
.card-header h6 {
  font-weight: 600;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.table td {
  vertical-align: middle;
}
</style>
